import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import { autocompleteDropdownStyles } from "Constants/Style";
import { City, Country, State } from "country-state-city";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import "react-country-state-city/dist/react-country-state-city.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddOrganisation = () => {
  const { id } = useParams<{ id: string }>();
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [formData, setFormData] = useState({
    organization_name: "",
    org_address: "",
    industry_type: "",
    org_city: "",
    org_state: "",
    org_country: "",
    org_pincode: "",
  });

  const commonInputStyle = {
    fontSize: "0.875rem",
    fontFamily: "Poppins, sans-serif",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };
  const commonLabelStyle = {
    fontSize: "0.8rem",
    fontFamily: "Poppins, sans-serif",
    marginBottom: 6,
    marginTop: 12,
  };
  const commonErrorStyle = {
    fontSize: "0.75rem",
    fontFamily: "Poppins, sans-serif",
    color: "#dc3545",
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [pincodeOptions, setPincodeOptions] = useState<string[]>([]);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [industryOptions, setIndustryOptions] = useState<string[]>([]);
  const [industryLoading, setIndustryLoading] = useState(false);
  const navigate = useNavigate();
  const [selectedCountryId, setSelectedCountryId] = useState<any>("");
  const [selectedStateId, setSelectedStateId] = useState<any>("");
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [toastState, setToastState] = useState(true);

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    if (name === "org_pincode") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
      setFormData((prev) => ({ ...prev, org_pincode: digitsOnly }));
      // Clear error for this field
      if (errors.org_pincode) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.org_pincode;
          return newErrors;
        });
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const requiredLabel = (label: string) => (
    <>
      {label} <span style={{ color: "red" }}>*</span>
    </>
  );

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.organization_name.trim())
      newErrors.organization_name = "Organisation Name is required";
    if (!formData.org_address.trim())
      newErrors.org_address = "Address is required";
    if (!formData.industry_type.trim())
      newErrors.industry_type = "Industry is required";
    if (formData.org_city.trim() === "")
      newErrors.org_city = "City is required";
    if (formData.org_state.trim() === "")
      newErrors.org_state = "State is required";
    if (formData.org_country.trim() === "")
      newErrors.org_country = "Country is required";
    if (!formData?.org_pincode) newErrors.org_pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(String(formData?.org_pincode).trim()))
      newErrors.org_pincode = "Pincode must be 6 digits";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      if (toastState)
        toast.error("Please enter all required fields correctly.");
      setErrors(validationErrors);
      setToastState(false);
      return;
    }
    setIsSubmitting(true);
    try {
      let response;
      if (id) {
        response = await Axios.put(`/organization/${id}/update/`, formData);
      } else {
        response = await Axios.post("/organization/create/", formData);
      }
      if (response.status === 200 || response.status === 201) {
        toast.success(
          `Organisation ${id ? "updated" : "created"} successfully`
        );

        // Clear state when creating new item, preserve when editing
        navigate("/organisations-list", { state: { clearState: !id } });
      }
    } catch (error: any) {
      const orgNameError = error?.response?.data?.error?.organization_name?.[0];
      const genericError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to save organisation";
      toast.error(orgNameError || genericError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelButton = () => {
    setFormData({
      organization_name: "",
      org_address: "",
      industry_type: "IT Services",
      org_city: "",
      org_state: "",
      org_country: "",
      org_pincode: "",
    });
    setErrors({});
    navigate("/organisations-list");
  };

  useEffect(() => {
    const countryList = Country.getAllCountries();
    // console.log("countryli: ", countryList);
    setCountries(countryList);

    // Auto-select India for new forms (not when editing)
    if (!id) {
      const india = countryList.find((c: any) => c.name === "India");
      if (india) {
        setFormData((prev) => ({
          ...prev,
          org_country: "India",
        }));
        // Load states for India
        const indianStates = State.getStatesOfCountry(india.isoCode);
        setStates(indianStates);
      }
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      Axios.get(`/organization/${id}/get`)
        .then((res) => {
          const response = res.data.data;
          const allCountries = Country.getAllCountries();
          const selectedCountry = allCountries.find(
            (c) => c.name === response.org_country
          );
          const countryIsoCode = selectedCountry?.isoCode || "";
          const statesOfCountry = State.getStatesOfCountry(countryIsoCode);
          const selectedState = statesOfCountry.find(
            (s) => s.name === response.org_state
          );
          const stateIsoCode = selectedState?.isoCode || "";

          const citiesOfState = City.getCitiesOfState(
            countryIsoCode,
            stateIsoCode
          );
          setCountries(allCountries);
          setStates(statesOfCountry);
          setCities(citiesOfState);
          setFormData({
            organization_name: response.organization_name || "",
            org_address: response.org_address || "",
            industry_type: response.industry_type || "",
            org_city: response.org_city || "",
            org_state: response.org_state || "",
            org_country: response.org_country || "",
            org_pincode:
              response.org_pincode !== undefined &&
              response.org_pincode !== null
                ? String(response.org_pincode)
                : "",
          });
        })
        .catch(() => {
          toast.error("Failed to fetch organisation details");
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const safeIndustryOptions = useMemo(() => {
    if (
      formData.industry_type &&
      !industryOptions.includes(formData.industry_type)
    ) {
      return [formData.industry_type, ...industryOptions];
    }
    return industryOptions;
  }, [formData.industry_type, industryOptions]);

  const LoadingCard = () => (
    <div className="text-center py-5 bg-white rounded-3 shadow-sm">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  return (
    <PageContainer>
      <FormHeader
        title={id ? "Edit Organisation" : "Add Organisation"}
        firstLabel="Organisation"
        secondLabel={id ? "Edit Organisation" : "Add Organisation"}
        handleArrowClick={() => navigate("/organisations-list")}
      />

      {loading ? (
        <LoadingCard />
      ) : (
        <>
          <Form noValidate onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  {requiredLabel("Organisation Name")}
                </Form.Label>
                <Form.Control
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleChange}
                  isInvalid={!!errors.organization_name}
                  placeholder="Enter Organisation Name"
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid" style={commonErrorStyle}>
                  {errors.organization_name}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  {requiredLabel("Industry Type")}
                </Form.Label>
                <Form.Control
                  name="industry_type"
                  value={formData.industry_type}
                  onChange={handleChange}
                  isInvalid={!!errors.industry_type}
                  placeholder="Enter Organisation Name"
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid" style={commonErrorStyle}>
                  {errors.industry_type}
                </Form.Control.Feedback>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  {requiredLabel("Address")}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    name="org_address"
                    value={formData.org_address}
                    onChange={handleChange}
                    isInvalid={!!errors.org_address}
                    placeholder="Enter Address"
                    style={commonInputStyle}
                  />
                  {/* <InputGroup.Text>
                <BsSearch />
              </InputGroup.Text> */}
                  <Form.Control.Feedback
                    type="invalid"
                    style={commonErrorStyle}
                  >
                    {errors.org_address}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col md={4} xs={12} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Country <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  disablePortal
                  options={countries}
                  getOptionLabel={(option) => option.name}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  value={
                    countries.find(
                      (c: any) => c.name === formData.org_country
                    ) || null
                  }
                  onChange={(e, selectedCountry) => {
                    setFormData((prev) => ({
                      ...prev,
                      org_country: selectedCountry?.name || "",
                      org_state: "",
                      org_city: "",
                    }));

                    // Clear errors for country, state, and city when country changes
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.org_country;
                      delete newErrors.org_state;
                      delete newErrors.org_city;
                      return newErrors;
                    });

                    if (selectedCountry) {
                      setIsLoadingStates(true);
                      const newStates = State.getStatesOfCountry(
                        selectedCountry.isoCode
                      );
                      setStates(newStates);
                      setCities([]);
                      setIsLoadingStates(false);
                    }
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Country"
                      size="small"
                      error={!!errors.org_country}
                      helperText={errors.org_country}
                      fullWidth
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                />
              </Col>

              <Col md={4} xs={12} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  State <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  disablePortal
                  options={states}
                  getOptionLabel={(option) => option.name}
                  loading={isLoadingStates}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  value={
                    states.find((s: any) => s.name === formData.org_state) ||
                    null
                  }
                  onChange={(e, selectedState) => {
                    const selectedCountry = countries.find(
                      (c: any) => c.name === formData.org_country
                    );

                    setFormData((prev) => ({
                      ...prev,
                      org_state: selectedState?.name || "",
                      org_city: "",
                    }));

                    // Clear errors for state and city when state changes
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.org_state;
                      delete newErrors.org_city;
                      return newErrors;
                    });

                    if (selectedCountry && selectedState) {
                      setIsLoadingCities(true);
                      const newCities = City.getCitiesOfState(
                        selectedCountry.isoCode,
                        selectedState.isoCode
                      );
                      setCities(newCities);
                      setIsLoadingCities(false);
                    }
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select State"
                      size="small"
                      error={!!errors.org_state}
                      helperText={errors.org_state}
                      fullWidth
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                  disabled={!formData.org_country}
                />
              </Col>

              <Col md={4} xs={12} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  City <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  disablePortal
                  options={cities}
                  getOptionLabel={(option) => option.name}
                  loading={isLoadingCities}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  value={
                    cities.find((c: any) => c.name === formData.org_city) ||
                    null
                  }
                  onChange={(e, selectedCity) => {
                    setFormData((prev) => ({
                      ...prev,
                      org_city: selectedCity?.name || "",
                    }));
                    // Clear error for city when city changes
                    if (errors.org_city) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.org_city;
                        return newErrors;
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select City"
                      size="small"
                      error={!!errors.org_city}
                      helperText={errors.org_city}
                      fullWidth
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                  disabled={!formData.org_state}
                />
              </Col>
              <Col md={4} xs={12} className="mb-4 mb-md-0">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Pincode <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  maxLength={6}
                  type="text"
                  name="org_pincode"
                  placeholder="Enter Pincode"
                  value={formData.org_pincode}
                  onChange={handleChange}
                  isInvalid={!!errors.org_pincode}
                  size="sm"
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid" style={commonErrorStyle}>
                  {errors.org_pincode}
                </Form.Control.Feedback>
              </Col>
            </Row>

            {/* Buttons */}
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
              <CancelBackButton onClick={handleCancelButton} />

              {/* Reusable submit button with spinner */}
              <SubmitButton isSubmitting={isSubmitting} isEdit={!!id} />
            </div>
          </Form>
        </>
      )}
    </PageContainer>
  );
};

export default AddOrganisation;
